import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  Input,
  Link as ChakraLink,
  Radio,
  RadioGroup,
  Stack,
  Text,
  FormLabel,
  FormControl,
} from "@chakra-ui/react";
import { useUser, IdentityType } from "@/contexts/UserContext";
import { Registry } from "../Registry";
import { useTranslation, Trans } from "react-i18next";
import { LocalizedLink } from "../i18n/LocalizedLink";

export const UnauthedHome = () => {
  const { user, login, signup } = useUser();
  const [email, setEmail] = useState("");
  const [identityType, setIdentityType] = useState<IdentityType>("patient");
  const [optInMarketing, setOptInMarketing] = useState(false);
  const { t, i18n } = useTranslation("home");

  return (
    <Grid
      templateColumns={["1fr", "1fr", "1fr", "1fr max-content"]}
      gap={[4, 16]}
      padding={[4, 6, 8]}
      marginInline="auto"
      alignItems="start"
    >
      <Stack spacing={4} maxWidth="xl" marginInline="auto">
        <Heading size={"xl"} lineHeight={1.2} letterSpacing="tight" fontWeight="regular">
          {t("unauthed.heading")}
        </Heading>
        <Text fontSize="lg">
          <Trans i18nKey="unauthed.description" ns="home" components={{ strong: <strong /> }} />
        </Text>

        {user ? (
          <Box
            padding={8}
            paddingBlockStart={6}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.500"
            backgroundColor="gray.50"
          >
            <Text fontSize="xl">{t("unauthed.signedInAs", { email: user.email })}</Text>
            <Button
              as={LocalizedLink}
              to="/registry"
              marginBlockStart={4}
              colorScheme="blue"
              variant="outline"
              backgroundColor="white"
              size="lg"
            >
              {t("unauthed.continueToApplication")} &rarr;
            </Button>
          </Box>
        ) : (
          <>
            <Stack as="form" marginTop={2} spacing={3}>
              <FormControl>
                <FormLabel fontSize="lg">{t("unauthed.emailLabel")}</FormLabel>
                <Input
                  type="email"
                  placeholder={t("unauthed.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="lg"
                  fontSize="lg"
                  padding={4}
                  borderColor="gray.500"
                  _placeholder={{ color: "gray.500" }}
                />
              </FormControl>
              <FormControl marginBlock={4}>
                <FormLabel fontSize="lg">{t("unauthed.identityLabel")}</FormLabel>
                <RadioGroup
                  value={identityType}
                  onChange={(value) => setIdentityType(value as IdentityType)}
                >
                  <Stack direction="column" spacing={0.5}>
                    <Radio width="max-content" value="patient">
                      <Text fontSize="lg">{t("unauthed.identityPatient")}</Text>
                    </Radio>
                    <Radio width="max-content" value="medical_professional">
                      <Text fontSize="lg">{t("unauthed.identityMedical")}</Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
              <Checkbox
                isChecked={optInMarketing}
                marginBlockEnd={4}
                onChange={(e) => setOptInMarketing(e.target.checked)}
                size="lg"
                alignItems="flex-start"
              >
                <Text userSelect="text" fontSize="lg" marginBlockStart={-1} marginInlineEnd={4}>
                  <Trans
                    i18nKey="unauthed.marketingConsent"
                    ns="home"
                    components={{ strong: <strong /> }}
                  />{" "}
                  <Text
                    textDecoration="underline"
                    fontWeight="medium"
                    color="blue.500"
                    as="a"
                    href="https://science.xyz/legal/privacy"
                    target="_blank"
                    rel="noopener"
                  >
                    {t("unauthed.privacyPolicy")}
                  </Text>
                  .
                </Text>
              </Checkbox>
              <Button
                data-testid="create-account-button"
                onClick={() => signup(email || undefined, identityType, optInMarketing)}
                colorScheme="blue"
                className="plausible-event-name=Patient-Registry-Landing-Create-Account"
                size="lg"
                fontSize="xl"
                padding={8}
              >
                {t("unauthed.createAccount")}
              </Button>
            </Stack>
            <Text fontSize="lg" paddingBlockStart={12}>
              {t("unauthed.alreadyHaveAccount")}{" "}
              <ChakraLink
                href={i18n.language === "en" ? "/login" : `/${i18n.language}/login`}
                textDecoration="underline"
              >
                {t("unauthed.signIn")}
              </ChakraLink>
              .
            </Text>
            <hr />
            <Text fontSize="xl">
              {t("unauthed.questionsHelp")}{" "}
              <ChakraLink
                href="https://science.xyz/company/contact/intake/"
                textDecoration="underline"
              >
                {t("unauthed.contactUs")}
              </ChakraLink>
              .
            </Text>
          </>
        )}
      </Stack>
      <Stack as="figure" maxWidth="xl" marginInline="auto" width="full">
        <Image
          aspectRatio={0.81}
          objectFit="cover"
          width="100%"
          maxWidth={["none", "none", "xs"]}
          borderRadius="lg"
          src="/images/prima-actor.jpg"
          alt={t("unauthed.imageAlt")}
        />
        <Text as="figcaption" maxWidth={["none", "none", "xs"]}>
          {t("unauthed.imageCaption")}
        </Text>
      </Stack>
    </Grid>
  );
};
